import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";

export default function HostelEnrollment() {
  const [feeHeads, setFeeHeads] = useState([]);
  const [form, setForm] = useState({
    acyear_id: "",
    student_id: "",
    student_rollno: "",
    accomodation_type: "",
    enroll_date: "",
    vacant_date: "",
    member_type: "",
    fee_head_id: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };
  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      const u = sessionStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (err) {
      return null;
    }
  }, []);
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (!user) return;
    const fetchStudent = async () => {
      try {
        const url = `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}?college_id=${encodeURIComponent(user.college_id || "")}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = res.data?.data || res.data || null;
        if (data && data.student_id) {
          setForm((prev) => ({
            ...prev,
            student_id: data.student_id,
            student_rollno: data.student_rollno || "",
            college_id: data.college_id || user.college_id || "",
          }));
        }
      } catch (err) {
        showSnackbar(getErrorMessage(err, "Unable to load student details"), "error");
      }
    };
    fetchStudent();
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (!user || !form.college_id) return;
    const fetchAcademicYear = async () => {
      try {
        const url = `${API_BASE_URL}/api/acyear/by-college/${user.client_id}/${form.college_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = res.data?.data || res.data || null;
        if (Array.isArray(data) && data.length > 0) {
          setForm((prev) => ({ ...prev, acyear_id: data[0].acyear_id }));
        }
      } catch (err) {
        showSnackbar(getErrorMessage(err, "Unable to load academic year"), "error");
      }
    };
    fetchAcademicYear();
  }, [user, form.college_id, API_BASE_URL]);

  const fetchEnrollments = useCallback(async () => {
    if (!form.student_id || !user?.client_id) return;
    setLoading(true);
    try {
      const url =
        `${API_BASE_URL}/api/hostelenrollment/list?student_id=` +
        form.student_id +
        `&client_id=${user?.client_id || ""}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRows(data);
    } catch (err) {
      setRows([]);
      showSnackbar(getErrorMessage(err, "Unable to load hostel enrollments"), "error");
    }
    setLoading(false);
  }, [form.student_id, user, API_BASE_URL]);

  useEffect(() => {
    if (form.student_id) fetchEnrollments();
  }, [form.student_id, fetchEnrollments]);

  useEffect(() => {
    if (!user || !form.college_id) return;
    const fetchFeeHeads = async () => {
      try {
        const url = `${API_BASE_URL}/api/fee-heads/${user.client_id}/${form.college_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setFeeHeads(data);
      } catch (err) {
        setFeeHeads([]);
        showSnackbar(getErrorMessage(err, "Unable to load fee heads"), "error");
      }
    };
    fetchFeeHeads();
  }, [user, form.college_id, API_BASE_URL]);
  const accomodationTypes = [
    { value: "PAID", label: "Paid" },
    { value: "FREE", label: "Free" },
  ];
  const memberTypes = [
    { value: "STUDENT", label: "Student" },
    { value: "STAFF", label: "Staff" },
  ];
  const formatDate = (val) => {
    if (!val) return "";
    const d = typeof val === "string" ? new Date(val) : val;
    return d && !isNaN(d) ? d.toLocaleDateString() : "";
  };

  const getFeeHeadName = (feeHeadId, row) => {
    if (row?.fee_head?.fee_head_name) return row.fee_head.fee_head_name;
    const feeHead = feeHeads.find((fh) => String(fh.fee_head_id) === String(feeHeadId));
    return feeHead?.fee_head_name || feeHeadId || "";
  };

  const handleDelete = async (row) => {
    if (!row?.hostel_enrollment_id || !user?.client_id) {
      showSnackbar("Enrollment details are missing", "error");
      return;
    }
    const result = await Swal.fire({
      title: "Delete enrollment?",
      text: "This will remove the hostel enrollment from the active list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      await axios.delete(
        `${API_BASE_URL}/api/hostelenrollment/${row.hostel_enrollment_id}?client_id=${user.client_id}`,
        { headers: getAuthHeaders() }
      );
      showSnackbar("Enrollment deleted successfully");
      fetchEnrollments();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "Delete failed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "serial_no",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "student_rollno", headerName: "Roll No", flex: 1, minWidth: 100 },
    {
      field: "accomodation_type",
      headerName: "Accommodation Type",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "enroll_date",
      headerName: "Enroll Date",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => formatDate(params?.row?.enroll_date),
    },
    {
      field: "created_on",
      headerName: "Created On (DB)",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => formatDate(params?.row?.created_on),
    },
    {
      field: "vacant_date",
      headerName: "Vacant Date",
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => formatDate(params?.row?.vacant_date),
    },
    { field: "member_type", headerName: "Member Type", flex: 1, minWidth: 120 },
    {
      field: "fee_head_id",
      headerName: "Fee Head",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => getFeeHeadName(params?.row?.fee_head_id, params?.row),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete enrollment">
          <IconButton color="error" size="medium" onClick={() => handleDelete(params.row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Form submit handler with SweetAlert confirmation
  const handleEnroll = async () => {
    if (!form.fee_head_id || !form.accomodation_type || !form.member_type) {
      showSnackbar("Please select all fields", "error");
      return;
    }
    if (!form.acyear_id || Number(form.acyear_id) === 0) {
      showSnackbar("Academic Year is not set. Please wait or select a valid year.", "error");
      return;
    }
    if (!user) {
      showSnackbar("User not found", "error");
      return;
    }
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to enroll?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, enroll",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const payload = {
        client_id: user.client_id,
        acyear_id: Number(form.acyear_id),
        student_id: Number(form.student_id),
        student_rollno: form.student_rollno,
        accomodation_type: form.accomodation_type,
        enroll_date: new Date().toISOString(),
        member_type: form.member_type,
        fee_head_id: Number(form.fee_head_id),
        created_by: user.user_email || null,
      };
      const url = `${API_BASE_URL}/api/hostelenrollment`;
      await axios.post(url, payload, { headers: getAuthHeaders() });
      showSnackbar("Enrollment successful");
      setForm((f) => ({
        ...f,
        fee_head_id: "",
        accomodation_type: "",
        member_type: "",
      }));
      fetchEnrollments(); // Refresh DataGrid after enroll
    } catch (err) {
      showSnackbar(getErrorMessage(err, "Enrollment failed"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            HOSTEL ENROLLMENT
          </Typography>
        </Toolbar>
        <Box p={2}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
              <TextField
                select
                margin="normal"
                label="Fee Head"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fee_head_id}
                onChange={(e) => setForm((p) => ({ ...p, fee_head_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Fee Head</option>
                {feeHeads.map((fh) => (
                  <option key={fh.fee_head_id} value={fh.fee_head_id}>
                    {fh.fee_head_name}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                margin="normal"
                label="Accommodation Type"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.accomodation_type}
                onChange={(e) => setForm((p) => ({ ...p, accomodation_type: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Accommodation Type</option>
                {accomodationTypes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                margin="normal"
                label="Member Type"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.member_type}
                onChange={(e) => setForm((p) => ({ ...p, member_type: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Member Type</option>
                {memberTypes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </TextField>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEnroll}
                disabled={loading}
                sx={{ height: "56px", minWidth: "160px", ml: { sm: 2, xs: 0 }, color: "#fff" }}
              >
                ENROLL NOW !
              </Button>
            </Stack>
            <DataGrid
              columns={columns}
              autoHeight
              loading={loading}
              getRowId={(row) => row.hostel_enrollment_id}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              rows={rows}
            />
          </Stack>
        </Box>
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
